import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  Alert,
  Anchor,
  Button,
  Center,
  Container,
  Group,
  Loader,
  Paper,
  SegmentedControl,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { IconRefresh } from '@tabler/icons-react';

import {
  CostByCategoryChart,
  CostByProviderChart,
  LogoPaper,
  PageTitle,
  ProjectEconomicsDrawer,
  ProjectEconomicsTable,
} from '@/components';
import { formatAmount } from '@/components/Cogs/palette';
import {
  useDisplayCurrency,
  useEconomicsRefresh,
  useEconomicsSummary,
  useProjectEconomics,
  useTeams,
  useUnattributedCosts,
} from '@/hooks';
import {
  DISPLAY_CURRENCIES,
  isIncomplete,
  marginShare,
  usageCoverage,
  type DisplayCurrency,
  type ProjectEconomics,
} from '@/types/economics';

type PeriodPreset = 'last-12' | 'this-year' | 'last-month' | 'custom';

const PAGE_SIZE = 50;

function iso(date: Date): string {
  return date.toISOString().slice(0, 10);
}

/** The inclusive ISO days a preset covers, by UTC calendar. */
export function presetPeriod(preset: PeriodPreset, today = new Date()): [string, string] {
  const y = today.getUTCFullYear();
  const m = today.getUTCMonth();
  switch (preset) {
    case 'this-year':
      return [`${y}-01-01`, iso(today)];
    case 'last-month':
      return [iso(new Date(Date.UTC(y, m - 1, 1))), iso(new Date(Date.UTC(y, m, 0)))];
    default:
      return [iso(new Date(Date.UTC(y, m - 11, 1))), iso(today)];
  }
}

function StatTile({
  label,
  value,
  note,
}: Readonly<{ label: string; value: string; note?: string }>) {
  return (
    <Paper withBorder p="md">
      <Text size="sm" c="dimmed">
        {label}
      </Text>
      <Text size="xl" fw={600}>
        {value}
      </Text>
      {note ? (
        <Text size="xs" c="dimmed">
          {note}
        </Text>
      ) : null}
    </Paper>
  );
}

function percentText(share: number | null): string {
  return share === null
    ? '—'
    : `${(share * 100).toLocaleString('en-GB', { maximumFractionDigits: 1 })}%`;
}

/**
 * Admin: revenue against what the work cost — partner fees, LLM and data
 * provider usage, registry documents, unused prepaid pools — per piece of
 * work and in aggregate, in a display currency the viewer picks. Cost figures
 * are a floor wherever lines are unrated or unconverted, and say so.
 */
export function CogsDashboardPage() {
  const { teams } = useTeams();
  const [currency, setCurrency] = useDisplayCurrency();
  const [preset, setPreset] = useState<PeriodPreset>('last-12');
  const [custom, setCustom] = useState<[string, string]>(presetPeriod('last-12'));
  const [teamId, setTeamId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState<ProjectEconomics | null>(null);

  const [periodFrom, periodTo] = preset === 'custom' ? custom : presetPeriod(preset);
  const filters = {
    displayCurrency: currency,
    teamId: teamId ?? undefined,
    periodFrom,
    periodTo,
  };
  const summary = useEconomicsSummary(filters);
  const projects = useProjectEconomics({ ...filters, page, pageSize: PAGE_SIZE });
  const unattributed = useUnattributedCosts({ displayCurrency: currency, periodFrom, periodTo });
  const refresh = useEconomicsRefresh(async () => {
    await Promise.all([summary.refetch(), projects.refetch(), unattributed.refetch()]);
  });

  const teamNames = useMemo(
    () => Object.fromEntries(teams.map((team) => [String(team.id), team.name])),
    [teams]
  );
  const teamOptions = useMemo(
    () =>
      teams
        .map((team) => ({ value: String(team.id), label: team.name }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [teams]
  );
  // Any filter change starts the table again from its first page.
  const filtered =
    <T,>(set: (value: T) => void) =>
    (value: T) => {
      set(value);
      setPage(1);
    };

  const data = summary.summary;
  const money = (value: number) => formatAmount(value, currency);

  return (
    <Container size="xl" py="md">
      <ProjectEconomicsDrawer row={open} displayCurrency={currency} onClose={() => setOpen(null)} />
      <Stack>
        <Stack gap={4}>
          <PageTitle title="Cost of work" />
          <Text size="sm" c="dimmed" maw={780}>
            Revenue against what the work cost. Every amount keeps its own currency and is converted
            into the one chosen here at its own date, from the ECB&apos;s daily reference rates. The
            figures refresh every half hour.
          </Text>
        </Stack>

        <Group align="flex-end" gap="md">
          <Select
            label="Period"
            data={[
              { value: 'last-12', label: 'Last 12 months' },
              { value: 'this-year', label: 'This year' },
              { value: 'last-month', label: 'Last month' },
              { value: 'custom', label: 'Custom' },
            ]}
            value={preset}
            allowDeselect={false}
            onChange={(v) => filtered(setPreset)((v ?? 'last-12') as PeriodPreset)}
            w={170}
          />
          {preset === 'custom' ? (
            <>
              <TextInput
                type="date"
                label="From"
                value={custom[0]}
                onChange={(e) => filtered(setCustom)([e.currentTarget.value, custom[1]])}
              />
              <TextInput
                type="date"
                label="To"
                value={custom[1]}
                onChange={(e) => filtered(setCustom)([custom[0], e.currentTarget.value])}
              />
            </>
          ) : null}
          <Select
            label="Client"
            placeholder="All clients"
            clearable
            searchable
            data={teamOptions}
            value={teamId}
            onChange={filtered(setTeamId)}
            w={240}
          />
          <Stack gap={4}>
            <Text size="sm" fw={500}>
              Show in
            </Text>
            <SegmentedControl
              aria-label="Display currency"
              value={currency}
              onChange={(v) => filtered(setCurrency)(v as DisplayCurrency)}
              data={DISPLAY_CURRENCIES.map((c) => ({ value: c, label: c }))}
            />
          </Stack>
          <Button
            variant="outline"
            leftSection={<IconRefresh size={16} />}
            loading={refresh.isRefreshing}
            onClick={() => void refresh.refresh()}
            ml="auto"
          >
            Refresh now
          </Button>
        </Group>

        {refresh.result?.backofficeError ? (
          <Alert color="yellow" title="Backoffice could not be read">
            The Hub&apos;s own costs were refreshed; contracts, Backoffice projects and their costs
            were left as they were. {refresh.result.backofficeError}
          </Alert>
        ) : null}
        {refresh.error ? (
          <Alert color="red" title="Refresh failed">
            {refresh.error.message}
          </Alert>
        ) : null}
        {summary.error ? (
          <Alert color="red" title="Could not load the figures">
            {summary.error.message}
          </Alert>
        ) : null}

        {data === null ? (
          <Center h={200}>{summary.isLoading ? <Loader /> : null}</Center>
        ) : (
          <>
            <SimpleGrid cols={{ base: 2, md: 4 }}>
              <StatTile label="Revenue" value={money(data.totals.revenue)} />
              <StatTile label="Cost" value={money(data.totals.cost)} />
              <StatTile
                label="Margin"
                value={money(data.totals.margin)}
                note={`${percentText(marginShare(data.totals))} of revenue`}
              />
              <StatTile
                label="Reports with recorded usage"
                value={percentText(usageCoverage(data.coverage))}
                note={`${data.coverage.reportsWithUsage} of ${data.coverage.completedReports} completed`}
              />
            </SimpleGrid>

            {isIncomplete(data.totals) ? (
              <Alert color="orange" title="Cost is a floor for this period">
                {data.totals.unrated > 0
                  ? `${data.totals.unrated} cost line(s) have no rate and are not counted. `
                  : ''}
                {data.totals.unconverted > 0
                  ? `${data.totals.unconverted} line(s) had no exchange rate and are left out. `
                  : ''}
                <Anchor component={Link} to="/admin/provider-rates" size="sm">
                  Add the missing rates
                </Anchor>
              </Alert>
            ) : null}

            <SimpleGrid cols={{ base: 1, lg: 2 }}>
              <LogoPaper>
                <Stack gap="sm">
                  <Title order={5}>Cost by month</Title>
                  <CostByCategoryChart months={data.byMonth} currency={currency} />
                </Stack>
              </LogoPaper>
              <LogoPaper>
                <Stack gap="sm">
                  <Title order={5}>Cost by provider</Title>
                  <CostByProviderChart providers={data.byProvider} currency={currency} />
                </Stack>
              </LogoPaper>
            </SimpleGrid>
          </>
        )}

        <LogoPaper>
          <Stack gap="sm">
            <Title order={5}>By piece of work</Title>
            {projects.error ? <Text c="red">{projects.error.message}</Text> : null}
            {projects.page ? (
              <ProjectEconomicsTable
                page={projects.page}
                teamNames={teamNames}
                onPage={setPage}
                onOpen={setOpen}
              />
            ) : (
              <Center h={120}>{projects.isLoading ? <Loader /> : null}</Center>
            )}
          </Stack>
        </LogoPaper>

        {data ? (
          <SimpleGrid cols={{ base: 1, lg: 2 }}>
            <LogoPaper>
              <Stack gap="sm">
                <Title order={5}>What the costs cannot see yet</Title>
                {data.coverage.unratedReasons.length === 0 ? (
                  <Text size="sm" c="dimmed">
                    Every cost line in this period is rated.
                  </Text>
                ) : (
                  <Table>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Reason</Table.Th>
                        <Table.Th ta="right">Lines</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {data.coverage.unratedReasons.map((r) => (
                        <Table.Tr key={r.reason}>
                          <Table.Td>{r.reason}</Table.Td>
                          <Table.Td ta="right">{r.lines}</Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                )}
              </Stack>
            </LogoPaper>
            <LogoPaper>
              <Stack gap="sm">
                <Title order={5}>Costs with no work behind them</Title>
                {unattributed.unattributed === null ||
                unattributed.unattributed.lines.length === 0 ? (
                  <Text size="sm" c="dimmed">
                    None in this period.
                  </Text>
                ) : (
                  <Table>
                    <Table.Tbody>
                      {unattributed.unattributed.lines.map((line) => (
                        <Table.Tr key={line.id}>
                          <Table.Td>{line.label ?? line.category}</Table.Td>
                          <Table.Td>{line.costDate}</Table.Td>
                          <Table.Td ta="right">
                            {line.money?.converted
                              ? formatAmount(line.money.converted.amount, currency)
                              : (line.unratedReason ?? '—')}
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                )}
              </Stack>
            </LogoPaper>
          </SimpleGrid>
        ) : null}
      </Stack>
    </Container>
  );
}
